import { ref, onMounted, onUnmounted, nextTick } from 'vue';

// Shared registry to track all dropdown instances - opening one closes others
const dropdownRegistry = new Map<string, () => void>();

export function useDropdown(containerId: string) {
  const isOpen = ref(false);
  const focusedIndex = ref(-1);
  let triggerElement: HTMLElement | null = null;
  let menuItems: HTMLElement[] = [];

  const getMenuItems = (): HTMLElement[] => {
    const container = document.getElementById(containerId);
    if (!container) return [];
    // Select focusable menu items (links and buttons)
    return Array.from(
      container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [role="menuitem"]'
      )
    );
  };

  const focusItem = (index: number) => {
    menuItems = getMenuItems();
    if (menuItems.length === 0) return;

    // Wrap around
    if (index < 0) index = menuItems.length - 1;
    if (index >= menuItems.length) index = 0;

    focusedIndex.value = index;
    menuItems[index]?.focus();
  };

  // Close all other dropdowns in the registry
  const closeOthers = () => {
    dropdownRegistry.forEach((closeFn, id) => {
      if (id !== containerId) {
        closeFn();
      }
    });
  };

  const toggle = (event?: Event) => {
    // Use currentTarget (the element the listener is bound to) rather than
    // target (the deepest descendant clicked). Otherwise clicking an inner
    // icon stores the icon as triggerElement, and the next click on the
    // surrounding button area is treated as outside-the-trigger and closes
    // the dropdown. Also keeps `triggerElement.focus()` on close working —
    // a non-button descendant cannot receive focus.
    if (event?.currentTarget) {
      triggerElement = event.currentTarget as HTMLElement;
    }

    if (!isOpen.value) {
      // Close other dropdowns before opening this one
      closeOthers();
    }

    isOpen.value = !isOpen.value;

    if (isOpen.value) {
      // Focus first item when opening
      nextTick(() => {
        focusedIndex.value = -1;
        focusItem(0);
      });
    } else {
      focusedIndex.value = -1;
    }
  };

  const open = (event?: Event) => {
    if (event?.currentTarget) {
      triggerElement = event.currentTarget as HTMLElement;
    }
    // Close other dropdowns before opening this one
    closeOthers();
    isOpen.value = true;
    nextTick(() => {
      focusedIndex.value = -1;
      focusItem(0);
    });
  };

  const close = (returnFocus = true) => {
    isOpen.value = false;
    focusedIndex.value = -1;
    if (returnFocus && triggerElement) {
      triggerElement.focus();
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const container = document.getElementById(containerId);

    // Check if click is inside container or its trigger
    if (container?.contains(target)) return;
    if (triggerElement?.contains(target)) return;

    // Also check parent container (for menus inside wrapper divs)
    const parentContainer = container?.closest('[class*="-container"]');
    if (parentContainer?.contains(target)) return;

    close(false);
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (!isOpen.value) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close(true);
        break;

      case 'ArrowDown':
        event.preventDefault();
        focusItem(focusedIndex.value + 1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        focusItem(focusedIndex.value - 1);
        break;

      case 'Home':
        event.preventDefault();
        focusItem(0);
        break;

      case 'End':
        event.preventDefault();
        menuItems = getMenuItems();
        focusItem(menuItems.length - 1);
        break;

      case 'Tab':
        // Close dropdown when tabbing out
        close(false);
        break;
    }
  };

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    // Register this dropdown so others can close it
    dropdownRegistry.set(containerId, () => close(false));
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('keydown', handleKeydown);
    // Unregister on unmount
    dropdownRegistry.delete(containerId);
  });

  return {
    isOpen,
    focusedIndex,
    toggle,
    open,
    close,
  };
}
